import { useMemo, useState } from 'react';
import { newIdempotencyKey } from '../api/client';
import type { Problem, Workspace } from '../api/types';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { ProblemNotice } from '../components/ProblemNotice';
import { usePlatform } from '../app/platform';
import { toProblem } from '../app/useAsync';
import type { Route } from '../app/router';

export interface WorkspacesPageProps {
  navigate(route: Route, options?: { replace?: boolean }): void;
}

/**
 * Workspaceの入口。一覧と検索はサイドバー（App.tsxのWorkspaceNav）が正本なので、
 * ここは一覧を重ねて出さず、説明と作成だけを受け持つ。
 */
export function WorkspacesPage({ navigate }: WorkspacesPageProps) {
  return (
    <div className="page">
      <header className="page-head">
        <h1>Workspace</h1>
        <p className="page-lead">
          目的やテーマごとの作業スペース。microappと業務データはこの単位で分かれます。
          左のサイドバーから選ぶか、ここで新しく作成してください。
        </p>
      </header>

      <CreateWorkspaceForm
        onCreated={(workspace) => navigate({ name: 'workspace', workspaceId: workspace.id })}
      />
    </div>
  );
}

function CreateWorkspaceForm({ onCreated }: { onCreated(workspace: Workspace): void }) {
  const { api } = usePlatform();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [problem, setProblem] = useState<Problem | null>(null);
  // 同じ作成操作の再送で二重作成しないよう、入力単位でキーを固定する。
  const idempotencyKey = useMemo(() => newIdempotencyKey(), []);

  const submit = async () => {
    setSubmitting(true);
    setProblem(null);
    try {
      const workspace = await api.createWorkspace(
        description ? { name, description } : { name },
        idempotencyKey,
      );
      onCreated(workspace);
    } catch (error) {
      setProblem(toProblem(error));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card title="Workspaceを作る">
      <form
        className="stack"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <label className="field">
          <span className="field-label">名前</span>
          <input
            className="input"
            value={name}
            required
            maxLength={100}
            onChange={(event) => setName(event.target.value)}
          />
        </label>
        <label className="field">
          <span className="field-label">説明（任意）</span>
          <textarea
            className="input"
            value={description}
            maxLength={2000}
            rows={2}
            onChange={(event) => setDescription(event.target.value)}
          />
        </label>
        <ProblemNotice problem={problem} />
        <div className="row">
          <Button type="submit" variant="primary" disabled={submitting || name.trim() === ''}>
            {submitting ? '作成中…' : '作成する'}
          </Button>
        </div>
      </form>
    </Card>
  );
}
